/**
 * Labstep
 *
 * @module services/location
 * @desc Service to manipulate URL
 */

import type { RouteComponentProps } from 'react-router-dom';

export class LocationService {
  static getQueryParameter(
    location: RouteComponentProps['location'],
    queryParameter: string,
  ): string | undefined {
    const params = new URLSearchParams(location.search);

    return params.get(queryParameter) || undefined;
  }

  static getQueryParameters(
    location: RouteComponentProps['location'],
  ): Record<string, unknown> {
    const params = new URLSearchParams(location.search);

    return Object.fromEntries(params);
  }

  static addQueryParameter(
    history: RouteComponentProps['history'],
    location: RouteComponentProps['location'],
    queryParameter: string,
    value: string,
  ): void {
    const params = new URLSearchParams(location.search);
    params.set(queryParameter, value);
    const newSearch = params.toString();
    history.push({
      ...location,
      search: newSearch,
    });
  }

  static addQueryParameters(
    history: RouteComponentProps['history'],
    location: RouteComponentProps['location'],
    params: Record<string, unknown>,
  ): void {
    const existingParams = new URLSearchParams(location.search);
    for (const key of Object.keys(params)) {
      if (params[key] !== undefined) {
        existingParams.set(key, String(params[key]));
      } else {
        existingParams.delete(key);
      }
    }
    const newSearch = existingParams.toString();
    history.push({
      ...location,
      search: newSearch,
    });
  }

  static removeQueryParameter(
    history: RouteComponentProps['history'],
    location: RouteComponentProps['location'],
    queryParameter: string,
  ): void {
    const params = new URLSearchParams(location.search);
    params.delete(queryParameter);
    const newSearch = params.toString();
    history.push({
      ...location,
      search: newSearch,
    });
  }

  static removeQueryParameters(
    history: RouteComponentProps['history'],
    location: RouteComponentProps['location'],
    queryParameters?: string[],
  ): void {
    if (queryParameters) {
      const params = new URLSearchParams(location.search);
      queryParameters.forEach((param) => params.delete(param));
      const newSearch = params.toString();
      history.push({
        ...location,
        search: newSearch,
      });
      return;
    }

    history.push({
      ...location,
      search: '',
    });
  }
}
